import { z } from "zod";
import { type } from "arktype";

export type StreamsResponse = typeof StreamsResponseSchema.infer;
export type Stream = typeof StreamSchema.infer;
export type BehaviorHints = typeof BehaviorHintsSchema.infer;

export const BehaviorHintsSchema = type({
  bingeGroup: "string?",
  filename: "string?",
});

export const StreamSchema = type({
  name: "string",
  title: "string",
  behaviorHints: BehaviorHintsSchema.optional(),
  sources: "string[]?",
}).and(
  type({
    url: type("string").pipe.try((url): string => {
      const urlObj = new URL(url);
      urlObj.pathname = `/proxy/${urlObj.hostname}${urlObj.pathname}`;
      return `${urlObj.pathname}${urlObj.search}`;
    }, type("string")),
  }).or(
    type({
      infoHash: "string",
      fileIdx: "number?",
      url: "never",
    })
  )
);

export const StreamsResponseSchema = type({
  streams: StreamSchema.array(),
  cacheMaxAge: "number",
  staleRevalidate: "number",
  staleError: "number",
});

export const getStreams = async (
  id: `movie/${string}` | `series/${string}`
) => {
  const res = await fetch(`/proxy/torrentio.strem.fun/stream/${id}.json`);
  const data = await res.json();
  const out = StreamsResponseSchema(data);
  if (out instanceof type.errors) {
    throw new Error(out.summary);
  }
  return out;
};

export const CINEMETA = "v3-cinemeta.strem.io";

export const ExtraSchema = type({
  name: "string",
  options: "string[]?",
  isRequired: "boolean?",
  optionsLimit: "number?",
});

export const CatalogSchema = type({
  type: "string",
  id: "string",
  genres: "string[]?",
  extra: ExtraSchema.array(),
  extraSupported: "string[]",
  extraRequired: "string[]?",
  name: "string",
});

export const AddonCatalogSchema = type({
  type: "string",
  id: "string",
  name: "string",
});

export const ManifestSchema = type({
  id: "string",
  version: "string",
  description: "string",
  name: "string",
  resources: "string[]",
  types: "string[]",
  idPrefixes: "string[]",
  addonCatalogs: AddonCatalogSchema.array(),
  catalogs: CatalogSchema.array(),
  behaviorHints: type({
    newEpisodeNotifications: "boolean",
  }),
});

export const getManifest = async (id: string) => {
  const res = await fetch(`/proxy/${CINEMETA}/manifest.json`);
  const data = await res.json();
  const out = ManifestSchema(data);
  if (out instanceof type.errors) throw new Error(out.summary);

  return out;
};

export const BaseMetaSchema = type({
  id: "string",
  name: "string",
  poster: "string.url",
  logo: "string.url?",
  background: "string.url?",
  year: "string?",
  runtime: "string|null?",
  description: "string|null?",
  genres: "string[]?",
  rating: "number?",
  releaseInfo: "string?",
});

export const SeriesMetaSchema = BaseMetaSchema.and({
  type: "'series'",
  videos: type({
    id: "string",
    name: "string?",
    season: "number",
    episode: "number",
    released: "string|null",
    description: "string?",
    thumbnail: "string.url?",
  })
    .array()
    .optional(),
  seasons: "number?",
  episodes: "number?",
});

export const MovieMetaSchema = BaseMetaSchema.and({
  type: "'movie'",
});

export const MetaSchema = type([MovieMetaSchema, "|", SeriesMetaSchema]);

export type Meta = typeof MetaSchema.infer;

export const CatalogResponseSchema = type({
  metas: MetaSchema.array(),
});

interface CatalogOptions {
  search?: string;
}

export async function getCatalog(
  id: `${"movie" | "series"}/${string}`,
  options?: CatalogOptions
) {
  const url = new URL(
    `${location.origin}/proxy/${CINEMETA}/catalog/${id}${
      options?.search ? `/search=${encodeURIComponent(options.search)}` : ""
    }.json`
  );

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(
      `Failed to fetch catalog: ${response.statusText} ${await response.text()}`
    );
  }
  const out = CatalogResponseSchema(await response.json());
  if (out instanceof type.errors) throw new Error(out.summary);
  return out;
}

export async function getMeta(catalog: "movie" | "series", id: `${string}`) {
  const url = new URL(
    `${location.origin}/proxy/${CINEMETA}/meta/${catalog}/${id}.json`
  );
  const response = await fetch(url.toString());
  if (!response.ok)
    throw new Error(
      `Failed to fetch meta: ${response.statusText} ${await response.text()}`
    );
  const data = await response.json();
  const out = type({ meta: MetaSchema })(data);
  if (out instanceof type.errors) throw new Error(out.summary);
  return out.meta;
}
